import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  REPORT_TYPES,
  RUN_ERROR_CODES,
  isListedCitation,
  NAME_MATCH_ONLY,
  numberReportSources,
  reportSourceKey,
  reportSourceWords,
  type ListedCitation,
  reportPassagePerson,
  type ReportCitation,
  type ReportDetail,
  type ReportPanel,
  type ReportPerson,
  type ReportSection,
  type ReportVersion,
} from '@ni/shared';
import { useMayWrite, useOrg } from '../../api/app-context';
import { errorLine, hasCode, isNotFound, loadErrorLine } from '../../api/errors';
import { useCreditPrice, useCreditShortfall, useRun } from '../../api/hooks';
import {
  useReport,
  useReportExport,
  useReportVersion,
  useReportWrites,
} from '../../api/report-hooks';
import { Claims } from '../../components/brief/claims';
import { ReportInputsForm } from '../../components/reports/report-inputs-form';
import { stripLine } from '../../components/run-strip/run-strip';
import { Checkbox } from '../../components/ui/checkbox';
import { Modal } from '../../components/ui/modal';
import { OverflowMenu } from '../../components/ui/overflow-menu';
import { IfWrites } from '../../components/write-access';
import { creditOperationFor } from '@ni/shared';
import { formatDate, formatDay } from '../../lib/format';
import { isTerminal } from '../../lib/run-state';
import {
  DELETED_PERSON,
  ORIGIN_LABELS,
  REPORTS_TITLE,
  personName,
  reportFlaggedOffRecord,
} from '../../lib/report-copy';
import {
  NOT_FOUND_LINE,
  pricedLabel,
  REPORT_REFRESH_HELP,
  REPORT_REVISE_HELP,
  RUN_CANCEL,
} from '../../lib/copy';
import { Loading } from '../../components/ui/loading';
import { Button } from '../../components/ui/button';
import './reports.css';
import { TopBar } from '../../components/ui/top-bar';

type Dialog = 'rewrite' | 'revise' | 'inputs' | 'refresh' | 'export' | 'archive' | null;

/** Keyed on the report, so moving between reports discards the page's state. */
export function ReportPage() {
  const { reportId = '' } = useParams();
  const org = useOrg();
  return <ReportContent key={`${org.id}-${reportId}`} reportId={reportId} />;
}

function ReportContent({ reportId }: { reportId: string }) {
  const org = useOrg();
  const report = useReport(reportId);
  const writes = useReportWrites(reportId);
  const mayWrite = useMayWrite('all');
  const { refetch: refetchReport } = report;
  const refetch = useCallback(() => void refetchReport(), [refetchReport]);
  /* The Analysis tab's Refresh lands here with the dialog open. */
  const [search, setSearch] = useSearchParams();
  const [dialog, setDialog] = useState<Dialog>(search.get('refresh') === '1' ? 'refresh' : null);
  // Once only: a reload or a shared link does not open it again.
  useEffect(() => {
    if (search.has('refresh')) setSearch({}, { replace: true });
  }, [search, setSearch]);
  const [viewing, setViewing] = useState<number | null>(null);
  const [comparing, setComparing] = useState(false);
  /* The export takes the version on screen: an older one while it is viewed.
     Same query as the older version's own view, so no second read. */
  const currentNumber = report.data?.current_version?.number ?? null;
  const olderNumber = viewing !== null && viewing !== currentNumber ? viewing : null;
  const older = useReportVersion(reportId, olderNumber);
  /* A travel brief's Update and Refresh collect again and are priced (credits
     launch spec step 2); a revise, and every other type, is free. */
  const price = useCreditPrice(creditOperationFor('report_refresh', { type: report.data?.type }));
  const short = useCreditShortfall(price.units);
  const priced = price.units === undefined ? {} : { expected_credit_units: price.units ?? 0 };

  if (report.isPending)
    return (
      <div className="column">
        <Loading />
      </div>
    );
  if (report.isError) {
    return (
      <div className="column">
        <p role="alert">
          {isNotFound(report.error) ? NOT_FOUND_LINE : loadErrorLine(report.error)}
        </p>
      </div>
    );
  }
  const detail = report.data;
  const current = detail.current_version;
  const busy = detail.active_run !== null;
  const archived = detail.state === 'archived';
  const type = REPORT_TYPES[detail.type];
  const editedSections = (current?.sections ?? []).filter(
    (s) => s.title_edited || s.passages.some((p) => p.edited),
  );
  const perPerson = type.default_sections.filter((s) => s.per_person).map((s) => s.key);

  /* The notices say what the office needs to know before reading: above the
     sheet, never on it, and never printed. */
  const notices = (
    <>
      {detail.active_run ? (
        <RunLine
          runId={detail.active_run.id}
          onCancel={() => writes.cancel.mutate(detail.active_run!.id)}
          onSettled={refetch}
        />
      ) : null}

      {detail.last_failure && !busy && !archived ? (
        <div className="report-notice is-attention" role="status">
          <span>
            {detail.last_failure.code === RUN_ERROR_CODES.REPORT_INPUT_MISSING
              ? detail.last_failure.message
              : `The report was not prepared. ${detail.last_failure.message}`}
          </span>
          <IfWrites>
            <Button variant="act" tiny onClick={() => setDialog('inputs')}>
              Change inputs
            </Button>
          </IfWrites>
        </div>
      ) : null}

      {detail.record_changed && !busy && !archived ? (
        <div className="report-notice" role="status">
          <span>A record in this report has changed since this version was written.</span>
          <IfWrites>
            <Button variant="act" tiny onClick={() => setDialog('refresh')}>
              Refresh
            </Button>
          </IfWrites>
        </div>
      ) : null}

      {archived ? (
        <div className="report-notice is-quiet">
          <span>This report is archived.</span>
        </div>
      ) : null}

      <WriteError writes={writes} />
    </>
  );

  return (
    <div className="column report-page">
      <TopBar
        parent={{ to: '/reports', label: REPORTS_TITLE }}
        here={<ReportCrumb detail={detail} onRename={(title) => writes.rename.mutate({ title })} />}
        actions={
          <>
            {!archived ? (
              <IfWrites>
                <Button variant="act" disabled={busy} onClick={() => setDialog('rewrite')}>
                  Rewrite
                </Button>
              </IfWrites>
            ) : null}
            {current ? (
              <Button variant="act" onClick={() => setDialog('export')}>
                Export
              </Button>
            ) : null}
            {!archived ? (
              <IfWrites>
                <OverflowMenu>
                  <button
                    type="button"
                    role="menuitem"
                    className="overflow-menu-item"
                    disabled={busy}
                    onClick={() => setDialog('archive')}
                  >
                    Archive
                  </button>
                </OverflowMenu>
              </IfWrites>
            ) : null}
          </>
        }
      />

      {current === null ? (
        <div className="report-main">
          {notices}
          <p className="lede">
            {busy ? 'The first version is being prepared.' : 'No version yet.'}
          </p>
        </div>
      ) : (
        <div className="report-wrap">
          <div className="report-main">
            {notices}
            {viewing !== null && viewing !== current.number ? (
              <OlderVersion
                reportId={reportId}
                number={viewing}
                detail={detail}
                orgName={org.name}
                perPerson={perPerson}
                onBack={() => setViewing(null)}
              />
            ) : comparing && current.number > 1 ? (
              <Compare
                reportId={reportId}
                current={current}
                detail={detail}
                perPerson={perPerson}
                onClose={() => setComparing(false)}
              />
            ) : (
              <ReportSheet detail={detail} version={current} orgName={org.name}>
                <Document
                  version={current}
                  people={detail.people}
                  perPerson={perPerson}
                  sectionsOff={detail.preferences.sections_off}
                  sourcesOff={detail.preferences.sources_off}
                  editable={!busy && !archived}
                  onSave={(baseVersionId, sections) =>
                    writes.edit.mutateAsync({ base_version_id: baseVersionId, sections }).then(
                      () => null,
                      (error: unknown) => editError(error),
                    )
                  }
                  saving={writes.edit.isPending}
                />
              </ReportSheet>
            )}
          </div>
          <aside className="report-aside">
            <Versions
              detail={detail}
              viewing={viewing ?? current.number}
              comparing={comparing}
              onView={(n) => {
                setComparing(false);
                setViewing(n === current.number ? null : n);
              }}
              onCompare={() => {
                setViewing(null);
                setComparing(true);
              }}
            />
            <PreparationPanel detail={detail} panel={current.panel} />
          </aside>
        </div>
      )}

      {dialog === 'rewrite' ? (
        <Modal label="Rewrite the report" onClose={() => setDialog(null)}>
          <RewriteChoice
            hasVersion={current !== null}
            onChoose={setDialog}
            onCancel={() => setDialog(null)}
          />
        </Modal>
      ) : null}
      {dialog === 'export' && current ? (
        <Modal label="Export the report" onClose={() => setDialog(null)}>
          <ExportForm
            reportId={reportId}
            documentsOnly={REPORT_TYPES[detail.type].export_sources === 'documents'}
            version={olderNumber !== null && !comparing ? (older.data ?? null) : current}
            versionError={olderNumber !== null && !comparing && older.isError ? older.error : null}
            current={olderNumber === null || comparing}
            sectionsOff={detail.preferences.sections_off}
            sourcesOff={detail.preferences.sources_off}
            disabled={archived || !mayWrite}
            saving={writes.preferences.isPending}
            onChange={(body) => writes.preferences.mutate(body)}
            onClose={() => setDialog(null)}
          />
        </Modal>
      ) : null}
      {dialog === 'revise' && current ? (
        <Modal label="Revise the report" onClose={() => setDialog(null)}>
          <ReviseForm
            sections={current.sections}
            edited={editedSections}
            pending={writes.revise.isPending}
            error={writes.revise.error}
            onSubmit={(body) => writes.revise.mutate(body, { onSuccess: () => setDialog(null) })}
            onCancel={() => setDialog(null)}
          />
        </Modal>
      ) : null}
      {dialog === 'inputs' ? (
        <Modal label="Change inputs" onClose={() => setDialog(null)}>
          <div className="report-modal">
            <h2>Change inputs</h2>
            <ReportInputsForm
              type={type}
              withTitle={false}
              initialPeople={detail.people
                .filter((p) => !p.deleted)
                .map((p) => ({ entity_id: p.entity_id, name: personName(p) }))}
              initialInputs={detail.inputs}
              fixedAccountId={detail.account?.id ?? null}
              withEvent={false}
              initialEventId={detail.event?.entity_id ?? ''}
              submitLabel={pricedLabel('Prepare a new version', price.units, price.unitsPerCredit)}
              creditShort={short}
              pending={writes.updateInputs.isPending}
              error={writes.updateInputs.error}
              onSubmit={({ people, inputs }) =>
                writes.updateInputs.mutate(
                  { people: people.map((p) => p.entity_id), inputs, ...priced },
                  { onSuccess: () => setDialog(null) },
                )
              }
              onCancel={() => setDialog(null)}
            />
          </div>
        </Modal>
      ) : null}
      {dialog === 'refresh' ? (
        <Modal label="Refresh the report" onClose={() => setDialog(null)}>
          <ReleaseForm
            title="Refresh the report"
            lede={REPORT_REFRESH_HELP}
            edited={editedSections}
            submitLabel={pricedLabel('Refresh', price.units, price.unitsPerCredit)}
            creditShort={short}
            pending={writes.refresh.isPending}
            error={writes.refresh.error}
            onSubmit={(release) =>
              writes.refresh.mutate(
                { rewrite_edited: release, ...priced },
                { onSuccess: () => setDialog(null) },
              )
            }
            onCancel={() => setDialog(null)}
          />
        </Modal>
      ) : null}
      {dialog === 'archive' ? (
        <Modal label="Archive the report" onClose={() => setDialog(null)}>
          <div className="report-modal">
            <h2>Archive this report?</h2>
            <p>
              It leaves the Reports list and takes no new versions. Nothing about the people in it
              changes.
            </p>
            {writes.archive.error ? <p role="alert">{dialogError(writes.archive.error)}</p> : null}
            <div className="report-form-actions">
              <Button
                variant="danger"
                onClick={() =>
                  writes.archive.mutate(undefined, { onSuccess: () => setDialog(null) })
                }
              >
                Archive
              </Button>
              <Button variant="act" onClick={() => setDialog(null)}>
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}

/* ————————————————————————— head and status ————————————————————————— */

/**
 * The report's name in the top bar, with Rename beside it (writers only, on an
 * active report). The sheet below carries the title as the document prints it.
 */
function ReportCrumb({
  detail,
  onRename,
}: {
  detail: ReportDetail;
  onRename: (title: string) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(detail.title);
  if (renaming) {
    return (
      <form
        className="report-rename"
        onSubmit={(e) => {
          e.preventDefault();
          if (title.trim()) onRename(title.trim());
          setRenaming(false);
        }}
      >
        <input
          className="control"
          aria-label="Title"
          maxLength={200}
          value={title}
          autoFocus
          onChange={(e) => setTitle(e.target.value)}
        />
        <button type="submit" className="btn go tiny">
          Save
        </button>
        <Button variant="act" tiny onClick={() => setRenaming(false)}>
          Cancel
        </Button>
      </form>
    );
  }
  return (
    <span className="report-crumb">
      <span className="report-crumb-title">{detail.title}</span>
      {detail.state === 'active' ? (
        <IfWrites>
          <button
            type="button"
            className="report-rename-open"
            onClick={() => {
              setTitle(detail.title);
              setRenaming(true);
            }}
          >
            Rename
          </button>
        </IfWrites>
      ) : null}
    </span>
  );
}

/**
 * **The document as a sheet**, read the way the PDF prints it
 * (`server/src/modules/reports/report-export.ts`, `masthead` and `furniture`):
 * the client's name over a green rule, the kind of document, the title, the
 * facts row, the sections, and the confidential line at the foot.
 */
function ReportSheet({
  detail,
  version,
  orgName,
  children,
}: {
  detail: ReportDetail;
  version: ReportVersion;
  orgName: string;
  children: ReactNode;
}) {
  const facts = [
    ['Account', detail.account?.label],
    ['Event', detail.event?.name],
    ['Date', version.for_date ? formatDate(version.for_date) : null],
    ['Prepared', `${formatDay(version.created_at)}, version ${version.number}`],
  ].filter((fact): fact is [string, string] => Boolean(fact[1]));
  return (
    <div className="report-sheet">
      <div className="report-mast">
        <span className="report-mast-org">{orgName}</span>
        <span className="report-mast-conf">Confidential</span>
      </div>
      <p className="report-kicker">{REPORT_TYPES[detail.type].display_name}</p>
      <h1 className="report-title">{detail.title}</h1>
      <dl className="report-facts">
        {facts.map(([label, value]) => (
          <div key={label}>
            <dt className="sc">{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {children}
      <div className="report-sheet-foot">
        {`Confidential. Prepared for ${detail.account?.label ?? orgName}.`}
      </div>
    </div>
  );
}

function RunLine({
  runId,
  onCancel,
  onSettled,
}: {
  runId: string;
  onCancel: () => void;
  onSettled: () => void;
}) {
  const run = useRun(runId);
  const settled = run.data ? isTerminal(run.data.state) : false;
  /* The report read polls on its own clock; when the run finishes first, ask
     for the report at once so the page never says "ready" over "being
     prepared". */
  useEffect(() => {
    if (settled) onSettled();
  }, [settled, onSettled]);
  const line = run.data ? stripLine(run.data, false, 0) : 'Preparing the report…';
  return (
    <div className="report-notice is-quiet" role="status">
      <span>{line}</span>
      <IfWrites>
        <Button variant="act" tiny onClick={onCancel}>
          {RUN_CANCEL.action}
        </Button>
      </IfWrites>
    </div>
  );
}

function WriteError({ writes }: { writes: ReturnType<typeof useReportWrites> }) {
  /* The last write the office made speaks for the page: an older failure is
     not news once a later write went through. */
  // An edit's refusal is said in the editor, where the office's text still is.
  const { edit: _edit, ...others } = writes;
  const latest = Object.values(others)
    .filter((m) => m.submittedAt > 0)
    .sort((a, b) => b.submittedAt - a.submittedAt)[0];
  const failed = latest?.isError ? latest : null;
  if (!failed) return null;
  const line = hasCode(failed.error, 'RUN_ALREADY_ACTIVE')
    ? 'This report is already being prepared.'
    : hasCode(failed.error, 'REPORT_VERSION_STALE')
      ? 'A newer version was saved first. Your change was not saved; make it again on the new version.'
      : errorLine(failed.error);
  return (
    <p className="report-notice is-attention" role="alert">
      {line}
    </p>
  );
}

/** A refused write, said inside the dialog that made it. */
function dialogError(error: unknown): string {
  return hasCode(error, 'RUN_ALREADY_ACTIVE')
    ? 'This report is already being prepared.'
    : errorLine(error);
}

/** Why an edit was not saved, said so the office knows its text is still here. */
function editError(error: unknown): string {
  if (hasCode(error, 'REPORT_VERSION_STALE')) {
    return 'A newer version was saved. Copy your text, cancel, and edit the new version.';
  }
  if (hasCode(error, 'RUN_ALREADY_ACTIVE')) {
    return 'A new version is being prepared, so this edit cannot be saved now. Your text is still here.';
  }
  return `${errorLine(error)} Your text is still here.`;
}

/* ————————————————————————— the document ————————————————————————— */

export function Document({
  version,
  sectionsOff,
  sourcesOff = false,
  editable,
  onSave,
  saving,
  people = [],
  perPerson = [],
}: {
  version: ReportVersion;
  sectionsOff: readonly string[];
  /** The report's people, to name a per-person section's passages as the PDF does. */
  people?: readonly ReportPerson[];
  /** The type's per-person section keys (guest notes). */
  perPerson?: readonly string[];
  sourcesOff?: boolean;
  editable: boolean;
  /**
   * Save the office's text against the version the editor was opened on.
   * Resolves to null when saved, or to the sentence saying why not, in which
   * case the editor stays open with the text as typed.
   */
  onSave?: (
    baseVersionId: string,
    sections: { id: string; passages: { id: string; text: string }[] }[],
  ) => Promise<string | null>;
  saving?: boolean;
}) {
  /* The section open for editing and the version it was opened on: a save is
     made against that version, so a newer one saved meanwhile is refused as
     stale instead of being written over. */
  const [editing, setEditing] = useState<{ section: string; version: string } | null>(null);
  const off = new Set(sectionsOff);
  const sources = numberReportSources(version.sections);
  /* The sources the exported sections cite. Print drops the rest, so a printed
     Sources list matches the sections it prints; the page keeps its numbers. */
  const printed = new Set(
    numberReportSources(version.sections.filter((s) => !off.has(s.id))).listed.map(reportSourceKey),
  );
  return (
    <article className="report-document">
      {version.sections.map((section, i) => (
        <section
          key={section.id}
          className={off.has(section.id) ? 'report-section is-off' : 'report-section'}
        >
          <div className="report-section-head">
            <h2>
              <span className="report-section-no">{String(i + 1).padStart(2, '0')}</span>
              {section.title}
            </h2>
            {off.has(section.id) ? <span className="tag-faint">left out of the export</span> : null}
            {editable && onSave && editing?.section !== section.id ? (
              <IfWrites>
                <button
                  type="button"
                  className="report-sec-edit"
                  onClick={() => setEditing({ section: section.id, version: version.id })}
                >
                  Edit
                </button>
              </IfWrites>
            ) : null}
          </div>
          {/* Not closed when a run starts: the office's text stays, with Save
              paused until the new version lands (a save then is refused as
              stale, and the text is still here). */}
          {editing?.section === section.id && onSave ? (
            <SectionEditor
              section={section}
              paused={!editable}
              saving={saving ?? false}
              onCancel={() => setEditing(null)}
              onSave={async (passages) => {
                const refused = await onSave(editing.version, [{ id: section.id, passages }]);
                if (refused === null) setEditing(null);
                return refused;
              }}
            />
          ) : (
            byPerson(section.passages, perPerson.includes(section.key ?? ''), people).map(
              (group) => (
                <div key={group.passages[0]!.id} className={group.person ? 'report-person' : ''}>
                  {group.person ? (
                    <div className="report-person-head">
                      <div className="report-person-name">{group.person.name}</div>
                      {group.person.detail ? (
                        <div className="report-person-detail">{group.person.detail}</div>
                      ) : null}
                    </div>
                  ) : null}
                  {group.passages.map((passage) => (
                    <Passage
                      key={passage.id}
                      version={version}
                      passage={passage}
                      numbers={sources.numbers}
                    />
                  ))}
                </div>
              ),
            )
          )}
        </section>
      ))}
      <Sources version={version} listed={sources.listed} printed={printed} off={sourcesOff} />
    </article>
  );
}

/**
 * A section's passages in runs about one person, headed as the PDF heads them
 * (`reportPassagePerson`): an attendee's name and seat, or a guest note's
 * person. A passage about no one stands alone.
 */
function byPerson(
  passages: readonly ReportSection['passages'][number][],
  perPerson: boolean,
  people: readonly ReportPerson[],
) {
  const groups: {
    person: ReturnType<typeof reportPassagePerson>;
    passages: ReportSection['passages'][number][];
  }[] = [];
  for (const passage of passages) {
    const person = reportPassagePerson(passage, perPerson, people);
    const last = groups[groups.length - 1];
    if (last && person && last.person?.key === person.key) last.passages.push(passage);
    else if (last && !person && !last.person) last.passages.push(passage);
    else groups.push({ person, passages: [passage] });
  }
  return groups;
}

function Passage({
  version,
  passage,
  numbers,
}: {
  version: ReportVersion;
  passage: ReportSection['passages'][number];
  numbers: ReadonlyMap<string, number>;
}) {
  const marks = new Map<number, ListedCitation>();
  for (const citation of passage.citations) {
    if (!isListedCitation(citation)) continue;
    const n = numbers.get(reportSourceKey(citation))!;
    if (!marks.has(n)) marks.set(n, citation);
  }
  const office = passage.citations.filter((c) => !isListedCitation(c));
  return (
    <p className="report-passage">
      {passage.text}
      {[...marks]
        .sort(([a], [b]) => a - b)
        .map(([n, citation]) => (
          <Cite key={n} mark={String(n)}>
            <SourceLine version={version} citation={citation} />
          </Cite>
        ))}
      {/* The office's own prose behind a passage: shown to the office, never
          numbered or printed, as the PDF lists none of it. */}
      {office.length > 0 ? (
        <Cite mark="†" office>
          {office.map((c, i) => (
            <SourceLine key={i} version={version} citation={c} />
          ))}
        </Cite>
      ) : null}
      {passage.sensitive ? <span className="tag-faint">sensitive</span> : null}
      {passage.edited ? <span className="tag-faint">edited</span> : null}
    </p>
  );
}

/** A superscript mark whose source shows on hover or focus. */
function Cite({
  mark,
  office = false,
  children,
}: {
  mark: string;
  office?: boolean;
  children: ReactNode;
}) {
  return (
    <button type="button" className={office ? 'cite is-office' : 'cite'}>
      {mark}
      <span className="cite-pop">{children}</span>
    </button>
  );
}

/** One source in a citation's hover: what it is, and its date under it. */
function SourceLine({ version, citation }: { version: ReportVersion; citation: ReportCitation }) {
  const { label, when } = sourceWords(version, citation);
  /* The office's own note is not in the Sources list, so its words are here. */
  const note =
    citation.kind === 'note'
      ? version.cited.notes.find((n) => n.note_id === citation.note_id)
      : undefined;
  return (
    <span className="cite-line">
      {label}
      {when ? <small>{formatDate(when)}</small> : null}
      {note ? <q>{clip(note.text, NOTE_QUOTE_MAX)}</q> : null}
    </span>
  );
}

/** Longest stretch of a note quoted in its hover; the note itself is on the person's record. */
const NOTE_QUOTE_MAX = 200;

function clip(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/**
 * A source in a line: a listed source in the shared words the PDF uses
 * (`reportSourceWords`), the office's own prose in the page's.
 */
function sourceWords(
  version: ReportVersion,
  citation: ReportCitation,
): { label: string; when: string | null } {
  if (citation.kind === 'input') {
    return { label: `${fieldLabel(citation.field)}, as you wrote it`, when: null };
  }
  if (citation.kind === 'note') {
    const note = version.cited.notes.find((n) => n.note_id === citation.note_id);
    if (!note) return { label: 'A note no longer on record', when: null };
    return { label: `Your note on ${note.person_name ?? DELETED_PERSON}`, when: note.date };
  }
  const words = reportSourceWords(version, citation);
  return {
    label: words.nameOnly ? `${words.name} (${NAME_MATCH_ONLY})` : words.name,
    when: words.when,
  };
}

/** An endnote: the source, linked where a reader can open it, its date, and its words. */
function Citation({ version, citation }: { version: ReportVersion; citation: ListedCitation }) {
  const [open, setOpen] = useState(false);
  const words = reportSourceWords(version, citation);
  if (citation.kind === 'fact') {
    const fact = version.cited.facts.find((f) => f.lineage_id === citation.lineage_id);
    if (!fact) return <span>{words.name}</span>;
    return (
      <>
        <button type="button" className="src-toggle" onClick={() => setOpen((v) => !v)}>
          {words.name}
        </button>
        {open ? <Claims lineageId={fact.lineage_id} /> : null}
      </>
    );
  }
  return (
    <span className="report-excerpt">
      {words.url ? (
        <a className="link" href={words.url} target="_blank" rel="noreferrer noopener">
          {words.name}
        </a>
      ) : (
        words.name
      )}
      {words.when ? `, ${formatDate(words.when)}` : null}
      {words.nameOnly ? <span className="tag-faint">{NAME_MATCH_ONLY}</span> : null}
      {words.quote ? <q>{words.quote}</q> : null}
    </span>
  );
}

/** A prose field's own label; keys are unique across report types. */
function fieldLabel(key: string): string {
  for (const type of Object.values(REPORT_TYPES)) {
    const field = type.fields.find((f) => f.key === key);
    if (field) return field.label;
  }
  return 'Your text';
}

function hasSources(version: ReportVersion): boolean {
  const { facts, analyses, materials, findings, notes } = version.cited;
  return facts.length + analyses.length + materials.length + findings.length + notes.length > 0;
}

function Sources({
  version,
  listed,
  printed,
  off,
}: {
  version: ReportVersion;
  listed: readonly ListedCitation[];
  /** The sources an exported section cites; the others don't print. */
  printed: ReadonlySet<string>;
  off: boolean;
}) {
  if (listed.length === 0) return null;
  return (
    <section
      className={[
        'report-section report-sources',
        off ? 'is-off' : null,
        printed.size === 0 ? 'unprinted' : null,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="report-section-head">
        <h2>Sources</h2>
        {off ? <span className="tag-faint">left out of the export</span> : null}
      </div>
      <ol className="report-endnotes">
        {listed.map((citation, i) => (
          <li
            key={reportSourceKey(citation)}
            className={printed.has(reportSourceKey(citation)) ? undefined : 'unprinted'}
          >
            <span className="report-endnote-no">{i + 1}</span>
            <div>
              <Citation version={version} citation={citation} />
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function SectionEditor({
  section,
  paused,
  saving,
  onSave,
  onCancel,
}: {
  section: ReportSection;
  paused: boolean;
  saving: boolean;
  onSave: (passages: { id: string; text: string }[]) => Promise<string | null>;
  onCancel: () => void;
}) {
  const [texts, setTexts] = useState(section.passages.map((p) => ({ id: p.id, text: p.text })));
  const [refused, setRefused] = useState<string | null>(null);
  return (
    <div className="report-editor">
      {refused ? (
        <p className="report-editor-note is-attention" role="alert">
          {refused}
        </p>
      ) : null}
      {paused ? (
        <p className="report-editor-note" role="status">
          A new version is being prepared. Saving is paused; your text is kept.
        </p>
      ) : null}
      {texts.map((passage, i) => (
        <textarea
          key={passage.id}
          className="control"
          aria-label={`Passage ${i + 1}`}
          rows={Math.max(2, Math.ceil(passage.text.length / 72))}
          value={passage.text}
          onChange={(e) =>
            setTexts(texts.map((t) => (t.id === passage.id ? { ...t, text: e.target.value } : t)))
          }
        />
      ))}
      <button
        type="button"
        className="report-editor-add"
        onClick={() => setTexts([...texts, { id: `office-${Date.now().toString(36)}`, text: '' }])}
      >
        Add a passage
      </button>
      <div className="report-form-actions">
        <Button
          variant="go"
          tiny
          disabled={saving || paused}
          onClick={() => {
            void onSave(texts.filter((t) => t.text.trim().length > 0)).then(setRefused);
          }}
        >
          Save
        </Button>
        <Button variant="act" tiny onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/* ————————————————————————— the side column ————————————————————————— */

/**
 * What the export holds: each section and the Sources list, saved on the report
 * as they are ticked. The ticks are held here and each save sends the whole
 * choice, so two quick ticks cannot overwrite each other with a stale list.
 * Export PDF waits for the last save, so the file matches the ticks.
 */
function ExportForm({
  reportId,
  documentsOnly,
  version,
  versionError,
  current,
  sectionsOff,
  sourcesOff,
  disabled,
  saving,
  onChange,
  onClose,
}: {
  reportId: string;
  /** The exported Sources list names the documents behind the record, not the record's items. */
  documentsOnly: boolean;
  /** The version on screen; null while an older one is still loading. */
  version: ReportVersion | null;
  /** Why the older version on screen could not be read, if it could not. */
  versionError: unknown;
  current: boolean;
  sectionsOff: readonly string[];
  sourcesOff: boolean;
  disabled: boolean;
  saving: boolean;
  onChange: (body: { sections_off?: string[]; sources_off?: boolean }) => void;
  onClose: () => void;
}) {
  const [sectionsLeftOut, setSectionsLeftOut] = useState<string[]>([...sectionsOff]);
  const [sourcesLeftOut, setSourcesLeftOut] = useState(sourcesOff);
  const exporter = useReportExport(reportId);
  /* Whether the export has a Sources list: a documents-mode export names the
     firm for its notes, a record-mode one lists only what the page lists. */
  const withSources =
    version !== null &&
    (documentsOnly ? hasSources(version) : numberReportSources(version.sections).listed.length > 0);
  const off = new Set(sectionsLeftOut);
  if (!version) {
    return (
      <div className="report-modal">
        {versionError ? <p role="alert">{loadErrorLine(versionError)}</p> : <Loading />}
      </div>
    );
  }
  return (
    <div className="report-modal">
      <h2>Export</h2>
      {current ? null : (
        <p className="report-modal-lede">{`Exports version ${version.number}, the one you are viewing.`}</p>
      )}
      <fieldset className="report-export-parts" disabled={disabled}>
        <legend>Exported sections</legend>
        {version.sections.map((section, i) => (
          <Checkbox
            key={section.id}
            className="report-check report-export-part"
            checked={!off.has(section.id)}
            disabled={disabled}
            onChange={(on) => {
              const next = on
                ? sectionsLeftOut.filter((id) => id !== section.id)
                : [...sectionsLeftOut, section.id];
              setSectionsLeftOut(next);
              onChange({ sections_off: next });
            }}
          >
            <span className="report-section-no">{String(i + 1).padStart(2, '0')}</span>
            {section.title}
          </Checkbox>
        ))}
        {withSources ? (
          <Checkbox
            className="report-check report-export-part"
            checked={!sourcesLeftOut}
            disabled={disabled}
            onChange={(on) => {
              setSourcesLeftOut(!on);
              onChange({ sources_off: !on });
            }}
          >
            Sources
          </Checkbox>
        ) : null}
      </fieldset>
      {documentsOnly && withSources && !sourcesLeftOut ? (
        <p className="report-modal-note">
          The exported sources list the public documents behind each cited fact.
        </p>
      ) : null}
      {exporter.error ? <p role="alert">{errorLine(exporter.error)}</p> : null}
      <div className="report-form-actions">
        <Button
          variant="go"
          disabled={saving || exporter.isPending}
          onClick={() =>
            exporter.mutate(version.number, {
              onSuccess: ({ file, filename }) => saveFile(file, filename),
            })
          }
        >
          {exporter.isPending ? 'Preparing the PDF…' : 'Export PDF'}
        </Button>
        <Button variant="act" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}

/** Hand a fetched file to the browser as a download. */
function saveFile(file: Blob, filename: string) {
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  /* Revoked well after the click: Safari and older Firefox read the blob only
     once the handler has returned, and an early revoke loses the download. */
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** The ways to get a new version, each said plainly, opening its own form. */
function RewriteChoice({
  hasVersion,
  onChoose,
  onCancel,
}: {
  hasVersion: boolean;
  onChoose: (dialog: 'revise' | 'refresh' | 'inputs') => void;
  onCancel: () => void;
}) {
  const options = [
    {
      key: 'revise' as const,
      title: 'Revise the wording',
      line: REPORT_REVISE_HELP,
      action: 'Revise',
      needsVersion: true,
    },
    {
      key: 'refresh' as const,
      title: 'Refresh from the record',
      line: REPORT_REFRESH_HELP,
      action: 'Refresh',
      needsVersion: true,
    },
    {
      key: 'inputs' as const,
      title: 'Change inputs',
      line: 'Changes who and what the report covers, then prepares a new version.',
      action: 'Change inputs',
      needsVersion: false,
    },
  ];
  return (
    <div className="report-modal">
      <h2>Rewrite</h2>
      <p>Each writes a new version. The one you have now stays under Versions.</p>
      <ul className="report-options">
        {options.map((option) => (
          <li key={option.key}>
            <div>
              <h3>{option.title}</h3>
              <p>{option.line}</p>
            </div>
            <Button
              variant="act"
              disabled={option.needsVersion && !hasVersion}
              onClick={() => onChoose(option.key)}
            >
              {option.action}
            </Button>
          </li>
        ))}
      </ul>
      <div className="report-form-actions">
        <Button variant="act" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function Versions({
  detail,
  viewing,
  comparing,
  onView,
  onCompare,
}: {
  detail: ReportDetail;
  viewing: number;
  comparing: boolean;
  onView: (n: number) => void;
  onCompare: () => void;
}) {
  const latest = detail.current_version?.number ?? null;
  return (
    <div className="report-aside-block">
      <h3 className="sc report-aside-head">Versions</h3>
      <ul className="report-versions">
        {detail.versions.map((v) => (
          <li key={v.id}>
            <button
              type="button"
              className={
                v.number === viewing && !comparing ? 'report-version is-viewing' : 'report-version'
              }
              onClick={() => onView(v.number)}
            >
              <span>{`Version ${v.number}`}</span>
              {v.number === latest ? <span className="report-version-meta">current</span> : null}
              <span className="report-version-origin">
                {`${ORIGIN_LABELS[v.origin]} · ${formatDay(v.created_at)}`}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {detail.versions.length > 1 ? (
        <button type="button" className="report-aside-action" onClick={onCompare}>
          Compare with the previous version
        </button>
      ) : null}
    </div>
  );
}

/**
 * **For the person preparing the report, never exported** (design §2.1): what
 * changed, what was read from the prose, what could not be used, who has thin
 * records and who is not on the record. The actions here never run on their
 * own; each is a press on the person's own page (ruling 11).
 */
export function PreparationPanel({ detail, panel }: { detail: ReportDetail; panel: ReportPanel }) {
  const version = detail.current_version!;
  const names = new Map(detail.people.map((p) => [p.entity_id, personName(p)]));
  const blocks: [string, React.ReactNode][] = [];
  if (panel.changes.length > 0) {
    blocks.push(['What changed', <Lines key="c" lines={panel.changes} />]);
  }
  if (version.details.length > 0) {
    blocks.push([
      'What was read from your description',
      <Lines key="d" lines={version.details.map((d) => `${d.label}: ${d.value}`)} />,
    ]);
  }
  if (panel.ambiguities.length > 0) {
    blocks.push(['Read one of two ways', <Lines key="a" lines={panel.ambiguities} />]);
  }
  const cannot = [
    ...panel.cannot_use,
    ...(panel.dropped_deleted > 0
      ? [
          panel.dropped_deleted === 1
            ? 'One person was left out because their record was deleted.'
            : `${panel.dropped_deleted} people were left out because their records were deleted.`,
        ]
      : []),
  ];
  if (cannot.length > 0) blocks.push(['What could not be used', <Lines key="u" lines={cannot} />]);
  if (panel.thin_records.length > 0) {
    blocks.push([
      'Little on record',
      <ul key="t" className="report-panel-lines">
        {panel.thin_records.map((t) => (
          <li key={t.entity_id}>
            {names.get(t.entity_id) ?? DELETED_PERSON}
            {t.quick_search_only ? ' has had a quick search only. ' : ' has not been researched. '}
            <Link className="link" to={`/people/${t.entity_id}`}>
              Research on their record
            </Link>
          </li>
        ))}
      </ul>,
    ]);
  }
  if (panel.not_on_record.length > 0) {
    blocks.push([
      'Mentioned but not on record',
      <ul key="n" className="report-panel-lines">
        {panel.not_on_record.map((name) => (
          <li key={name}>
            {name}.{' '}
            <IfWrites>
              <Link className="link" to="/people/new">
                Add a person
              </Link>
            </IfWrites>
          </li>
        ))}
      </ul>,
    ]);
  }
  if (panel.edited_kept.length > 0) {
    blocks.push(['Your edits kept', <Lines key="e" lines={panel.edited_kept} />]);
  }
  const attendees = panel.attendees;
  if (detail.event && attendees) {
    const analysis = `/events/${detail.event.entity_id}/analysis`;
    const lines: React.ReactNode[] = [];
    if (attendees.to_settle > 0) {
      lines.push(
        <li key="settle">
          {attendees.to_settle === 1
            ? 'One attendee could not be told apart from a namesake with something relevant. '
            : `${attendees.to_settle} attendees could not be told apart from a namesake with something relevant. `}
          <Link className="link" to={analysis}>
            Say who they are
          </Link>
        </li>,
      );
    }
    if (attendees.not_assessed > 0) {
      lines.push(
        <li key="unassessed">
          {attendees.not_assessed === 1
            ? 'One attendee was not assessed. '
            : `${attendees.not_assessed} attendees were not assessed. `}
          <Link className="link" to={`/events/${detail.event.entity_id}/attendees`}>
            See the attendee list
          </Link>
        </li>,
      );
    }
    for (const flagged of attendees.flagged_off_record) {
      lines.push(
        <li key={flagged.node_id}>
          {reportFlaggedOffRecord(flagged.name, flagged.why)}
          <Link className="link" to={analysis}>
            Open the attendee
          </Link>
        </li>,
      );
    }
    if (lines.length > 0) {
      blocks.push([
        'Attendees',
        <ul key="att" className="report-panel-lines">
          {lines}
        </ul>,
      ]);
    }
  }

  return (
    <div className="report-aside-block report-panel">
      <h3 className="sc report-aside-head">For you, not in the report</h3>
      {blocks.length === 0 ? (
        <p className="report-panel-quiet">Nothing to note on this version.</p>
      ) : null}
      {blocks.map(([head, body]) => (
        <div key={head} className="report-panel-block">
          <div className="report-panel-head">{head}</div>
          {body}
        </div>
      ))}
    </div>
  );
}

function Lines({ lines }: { lines: string[] }) {
  return (
    <ul className="report-panel-lines">
      {lines.map((line, i) => (
        <li key={i}>{line}</li>
      ))}
    </ul>
  );
}

function OlderVersion({
  reportId,
  number,
  detail,
  orgName,
  perPerson,
  onBack,
}: {
  reportId: string;
  number: number;
  detail: ReportDetail;
  orgName: string;
  perPerson: readonly string[];
  onBack: () => void;
}) {
  const version = useReportVersion(reportId, number);
  return (
    <>
      <div className="report-notice">
        <span>{`Version ${number}, as it was written.`}</span>
        <Button variant="act" tiny onClick={onBack}>
          Back to the current version
        </Button>
      </div>
      {version.data ? (
        <ReportSheet detail={detail} version={version.data} orgName={orgName}>
          <Document
            version={version.data}
            people={detail.people}
            perPerson={perPerson}
            sectionsOff={[]}
            editable={false}
          />
        </ReportSheet>
      ) : null}
    </>
  );
}

/** The previous version beside the current one, each headed by its number, on one sheet. */
function Compare({
  reportId,
  current,
  detail,
  perPerson,
  onClose,
}: {
  reportId: string;
  current: ReportVersion;
  detail: ReportDetail;
  perPerson: readonly string[];
  onClose: () => void;
}) {
  const previous = useReportVersion(reportId, current.number - 1);
  return (
    <>
      <div className="report-notice">
        <span>{`Version ${current.number - 1} beside version ${current.number}.`}</span>
        <Button variant="act" tiny onClick={onClose}>
          Close
        </Button>
      </div>
      <div className="report-sheet report-compare">
        <div>
          <p className="report-compare-head">{`Version ${current.number - 1}`}</p>
          {previous.data ? (
            <Document
              version={previous.data}
              people={detail.people}
              perPerson={perPerson}
              sectionsOff={[]}
              editable={false}
            />
          ) : null}
        </div>
        <div>
          <p className="report-compare-head">{`Version ${current.number}`}</p>
          <Document
            version={current}
            people={detail.people}
            perPerson={perPerson}
            sectionsOff={[]}
            editable={false}
          />
        </div>
      </div>
    </>
  );
}

/* ————————————————————————— dialogs ————————————————————————— */

function ReviseForm({
  sections,
  edited,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  sections: ReportSection[];
  edited: ReportSection[];
  pending: boolean;
  error: unknown;
  onSubmit: (body: {
    instruction: string;
    section_id: string | null;
    keep_instruction: boolean;
    rewrite_edited: string[];
  }) => void;
  onCancel: () => void;
}) {
  const [instruction, setInstruction] = useState('');
  const [sectionId, setSectionId] = useState<string>('');
  const [keep, setKeep] = useState(false);
  const [release, setRelease] = useState<string[]>([]);
  const releasable = sectionId ? edited.filter((s) => s.id === sectionId) : edited;
  return (
    <form
      className="report-modal"
      onSubmit={(e) => {
        e.preventDefault();
        if (instruction.trim()) {
          onSubmit({
            instruction: instruction.trim(),
            section_id: sectionId || null,
            keep_instruction: keep,
            rewrite_edited: release.filter((id) => releasable.some((s) => s.id === id)),
          });
        }
      }}
    >
      <h2>Revise</h2>
      <p>{REPORT_REVISE_HELP}</p>
      <label className="field">
        <span className="label">What to change</span>
        <textarea
          className="control"
          rows={4}
          maxLength={2000}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
        />
      </label>
      <label className="field">
        <span className="label">Apply to</span>
        <select
          className="control"
          value={sectionId}
          onChange={(e) => setSectionId(e.target.value)}
        >
          <option value="">The whole report</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
            </option>
          ))}
        </select>
      </label>
      <Checkbox className="report-check" checked={keep} onChange={setKeep}>
        Keep applying this to later versions
      </Checkbox>
      <ReleaseChecks edited={releasable} release={release} onChange={setRelease} />
      {error ? <p role="alert">{dialogError(error)}</p> : null}
      <div className="report-form-actions">
        <button type="submit" className="btn go" disabled={pending || !instruction.trim()}>
          {pending ? 'Starting…' : 'Revise'}
        </button>
        <Button variant="act" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function ReleaseForm({
  title,
  lede,
  edited,
  submitLabel,
  creditShort,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  title: string;
  lede: string;
  edited: ReportSection[];
  submitLabel: string;
  creditShort: string | null;
  pending: boolean;
  error: unknown;
  onSubmit: (release: string[]) => void;
  onCancel: () => void;
}) {
  const [release, setRelease] = useState<string[]>([]);
  return (
    <div className="report-modal">
      <h2>{title}</h2>
      <p>{lede}</p>
      <ReleaseChecks edited={edited} release={release} onChange={setRelease} />
      {error ? <p role="alert">{dialogError(error)}</p> : null}
      <div className="report-form-actions">
        <Button
          variant="go"
          disabled={pending || creditShort !== null}
          onClick={() => onSubmit(release)}
        >
          {pending ? 'Starting…' : submitLabel}
        </Button>
        {creditShort ? <span className="credit-short">{creditShort}</span> : null}
        <Button variant="act" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** Sections the office edited stay as edited unless ticked here (design §3). */
function ReleaseChecks({
  edited,
  release,
  onChange,
}: {
  edited: ReportSection[];
  release: string[];
  onChange: (next: string[]) => void;
}) {
  if (edited.length === 0) return null;
  return (
    <fieldset className="report-release">
      <legend>Edited sections are kept unless selected.</legend>
      {edited.map((s) => (
        <Checkbox
          key={s.id}
          className="report-check"
          checked={release.includes(s.id)}
          onChange={(on) => onChange(on ? [...release, s.id] : release.filter((id) => id !== s.id))}
        >
          {`Rewrite ${s.title}`}
        </Checkbox>
      ))}
    </fieldset>
  );
}
